import React from "react";
import {
	BankAccountEntityType,
	ClientEntityType,
	JobDocumentEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { LieferscheinTemplate } from "../../pdf-templates/pdf-templates/lieferschein.template";
import { KostenvoranschlagTemplate } from "../../pdf-templates/pdf-templates/kostenvoranschlag.template";
import { RechnungTemplate } from "../../pdf-templates/pdf-templates/rechnung.template";
import { GutschriftTemplate } from "../../pdf-templates/pdf-templates/gutschrift.template";
import { BarzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/barzahlungsquittung.template";
import { KartenzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/kartenzahlungsquittung.template";
import { AkontorechnungTemplate } from "../../pdf-templates/pdf-templates/akontorechnung.template";
import { PaymentReminderTemplate } from "../../pdf-templates/pdf-templates/payment-reminder.template";
import { getPaymentReminderDocumentExample } from "../../constants/paymentReminderDocumentExample";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import { AuftragsblattTemplate } from "../../pdf-templates/pdf-templates/auftragsblatt.template";
import { JobOrderLabelTemplate } from "../../pdf-templates/pdf-templates/job-order-label.template";
import { PaymentSlipDataInput } from "../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { CalculateTotalsResult } from "./calculate";
import { CommonPDFTemplateProps } from "../../pdf-templates/pdf-templates/types";
import { Region } from "../../types/enums";
import { getDeliveryNoteTitle } from "../../pages/job-page/job-document/job-document.types";

const cr = (value: number): { text: string; value: number } => ({
	text: value.toFixed(2),
	value,
});

const ZERO = cr(0);

function getTestBaseData(
	organization: OrganizationEntityType,
	pdfTemplateConfiguration: PdfTemplateConfiguration
) {
	const today = new Date().toISOString().split("T")[0];

	const client: ClientEntityType = {
		id: "test-client",
		code: "K-1001",
		company_name: "Zahnarztpraxis Dr. Muster",
		is_company: true,
		title: null,
		first_name: "Max",
		last_name: "Muster",
		street: "Bahnhofstrasse 10",
		postal_code: "8001",
		city: "Zürich",
		country: "CH",
		email: "praxis@dr-muster.ch",
		phone_business: "044 123 45 67",
		phone_mobile: null,
		phone_personal: null,
		fax: null,
		additional_information: null,
		avatar_path: null,
		bank_account_id: null,
		billing_region: null,
		c_additional_information: null,
		c_city: null,
		c_company_name: null,
		c_country: null,
		c_email: null,
		c_fax: null,
		c_first_name: null,
		c_is_company: null,
		c_last_name: null,
		c_phone_business: null,
		c_phone_mobile: null,
		c_phone_personal: null,
		c_postal_code: null,
		c_street: null,
		c_title: null,
		connected_organization_id: null,
		created_at: null,
		currency: null,
		default_activated_region_id: null,
		default_catalogs_custom_id: null,
		default_catalogs_default_id: null,
		default_job_tabs_config_id: null,
		default_tp_value: null,
		default_tp_variation: null,
		del_note_print_qty: null,
		discount_material: null,
		discount_work: null,
		external_reference: null,
		eori_number: null,
		favorite: null,
		guarantor_id: null,
		inactive_reason: null,
		inv_footnote: null,
		inv_footnote_type: null,
		inv_print_qty: null,
		is_correspondence_address: false,
		is_guarantor_print_on_cn: null,
		is_guarantor_print_on_dn: null,
		is_guarantor_print_on_qt: null,
		is_inactive: false,
		is_incl_dr_tax: false,
		is_inv_w_job_title: null,
		is_inv_w_pat_data: null,
		is_monthly_discount: false,
		is_monthly_discount_only_work: false,
		is_mwst_eust: false,
		is_print_del_note_w_col_card: null,
		is_print_del_note_w_pat_pass: null,
		is_print_kv_w_col_card: null,
		is_print_pat_code_only: null,
		is_print_skonto: null,
		is_pt_name_in_doc_name: null,
		is_pt_name_in_xml_name: null,
		is_show_insurance_on_pdf: false,
		is_srg_grouped_by_prac: null,
		kun_id: null,
		kv_print_qty: null,
		language: null,
		modified_at: null,
		monthly_discount: null,
		mwst_number: null,
		mwst_percentage: null,
		note: null,
		organization_id: organization.id,
		patient_name_format: null,
		requests_checked: null,
		requests_text: null,
		solvency: null,
		srg_sorting_option: null,
		sv_number: null,
		vat_display_override: null,
		default_price_list_id: null,
		is_send_email_on_share: false,
		sumex_xml_version: "450",
	};

	const job: JobEntityType = {
		id: 99999,
		code: "T-0001",
		title: "Testauftrag",
		organization_id: organization.id ?? "",
		client_id: "test-client",
		status: 2,
		incoming_order_date: today,
		outgoing_order_date: today,
		incoming_order_time: null,
		outgoing_order_time: null,
		tooth_color: "A2",
		description: null,
		prod_country_choice: "ch",
		prod_country_other: null,
		patient_id: null,
		practitioner_id: null,
		guarantor_id: null,
		is_paid_by_patient: false,
		is_mwst_eust: false,
		mwst_number: null,
		invoice_id: null,
		employee_id: null,
		note: null,
		note_modified_at: null,
		bank_account_id: null,
		created_at: today,
		created_by: null,
		modified_at: today,
		modified_by: null,
		additional_information: null,
		credit_note_date: null,
		currency_text: null,
		del_note_data: null,
		exchange_ratio: null,
		fae_id: null,
		IGNORE_is_mwst: null,
		insurance: null,
		inv_covered_by: null,
		is_ignore_monthly_discount: false,
		is_via_email_del_note: false,
		is_via_email_kv: false,
		kv_total_final: null,
		legacy_tp_tier: 0,
		max_inv_value: null,
		monthly_count: 0,
		parent_job_id: null,
		price_list_id: null,
		quote_date: null,
		round_to: null,
		search_vector: null,
		shared_with: null,
		tax_rate: organization.mwst_percentage ?? 8.1,
		tp_value: null,
		tp_variation: 0,
	};

	const makeItem = (
		id: string,
		code: string,
		description: string,
		quantity: number,
		price: number,
		type: number
	): JobItemEntityType => ({
		id,
		job_document_id: 99999,
		code_e: code,
		code: code,
		description,
		quantity,
		price,
		tp_value: 0,
		type,
		discount: 0,
		lot: null,
		unit: null,
		note: null,
		additional_text: null,
		ce_number: null,
		cluster: null,
		composition: null,
		created_at: today,
		fpo_id: null,
		is_print_additional_text: null,
		is_req_dr_tax: false,
		is_warranty: false,
		legacy_article_id: null,
		legacy_tariff_id: null,
		legacy_tax_rate: null,
		manufacturer: null,
		modified_at: today,
		modified_by: null,
		profile_id: null,
		profile2_id: null,
		profile3_id: null,
		techn_perc_2: null,
		techn_perc_3: null,
		position: null,
	});

	const jobItems: JobItemEntityType[] = [
		makeItem("item-1", "ZK01", "Zirkonkrone", 1, 420.0, 1),
		makeItem("item-2", "KV01", "Keramikverblendung", 2, 185.0, 1),
		makeItem("item-3", "MD01", "Modell", 1, 45.0, 2),
	];

	const subtotal = 420.0 + 2 * 185.0 + 45.0; // 835.00
	const mwstPct = organization.mwst_percentage ?? 8.1;
	const mwstAmount = Math.round(subtotal * (mwstPct / 100) * 100) / 100;
	const total = subtotal + mwstAmount;

	const jobTotals: CalculateTotalsResult = {
		amountWorkAndFixedRate: cr(420.0 + 370.0),
		amountMaterials: cr(45.0),
		amountPostage: ZERO,
		amountExternalWork: ZERO,
		amountDiscounts: ZERO,
		deductions: ZERO,
		deductionsWorkAndFixedRate: ZERO,
		deductionsMaterials: ZERO,
		subTotal: cr(subtotal),
		mwst: cr(mwstAmount),
		total: cr(total),
		taxpunkte: ZERO,
		exemptFromTaxAmountExclVat: ZERO,
		exemptFromTaxAmountInclVat: ZERO,
		subjectToDrTaxAmountExclVat: ZERO,
		subjectToDrTaxAmountInclVat: ZERO,
		mwstPercentage: mwstPct.toString(),
	};

	return {
		today,
		client,
		job,
		jobItems,
		jobTotals,
		organization,
		pdfTemplateConfiguration,
	};
}

function getTestJobDocument(
	today: string,
	type: string,
	title: string
): JobDocumentEntityType {
	return {
		id: 99999,
		job_id: 99999,
		date: today,
		title,
		status: 1,
		type,
		amount: null,
		code: null,
		created_at: today,
		created_by: null,
		deleted_at: null,
		discount_material: null,
		discount_work: null,
		fae_id: null,
		modified_at: null,
		modified_by: null,
		note: null,
		type_data: null,
		work_title: null,
	};
}

function getTestPaymentSlipData(
	client: ClientEntityType
): PaymentSlipDataInput {
	const bankAccount: BankAccountEntityType = {
		id: 0,
		organization_id: "",
		account_name: "Testkonto",
		bank_name: "Testbank AG",
		bank_city: "Zürich",
		qr_iban: "CH4431999123000889012",
		currency: "CHF",
		pc_account: null,
		is_pc_account: false,
		letterhead: null,
		reference_number: "000000000000000000000000000",
		cdtr_name: "Testlabor GmbH",
		cdtr_adrtp: "S",
		cdtr_street_line_1: "Laborstrasse 1",
		cdtr_house_number_line_2: null,
		cdtr_area_code: "8001",
		cdtr_city: "Zürich",
		cdtr_country: "CH",
		created_at: null,
		modified_at: null,
	};

	return {
		bankAccount,
		recipient: client,
		amount: "835.00",
		currency: "CHF",
		invoiceNumber: "T-0001",
	};
}

function getBaseProps(
	organization: OrganizationEntityType,
	pdfTemplateConfiguration: PdfTemplateConfiguration,
	docType: string,
	docTitle: string
): CommonPDFTemplateProps & { today: string; client: ClientEntityType } {
	const base = getTestBaseData(organization, pdfTemplateConfiguration);
	const jobDocument = getTestJobDocument(base.today, docType, docTitle);
	return {
		organization: base.organization,
		pdfTemplateConfiguration: base.pdfTemplateConfiguration,
		client: base.client,
		job: base.job,
		jobDocument,
		jobItems: base.jobItems,
		jobTotals: base.jobTotals,
		today: base.today,
	};
}

const DOC_TYPE_MAP: Record<string, { type: string; title: string }> = {
	default: { type: "delivery_note", title: "Lieferschein" },
	ls: { type: "delivery_note", title: "Lieferschein" },
	kv: { type: "quote", title: "Kostenvoranschlag" },
	rg: { type: "invoice", title: "Rechnung" },
	rg_doppel: { type: "invoice", title: "Rechnungsdoppel" },
	gs: { type: "credit_note", title: "Gutschrift" },
	bar: { type: "cash_receipt", title: "Barzahlungsquittung" },
	karte: { type: "card_receipt", title: "Kartenzahlungsquittung" },
	akonto: { type: "advance_invoice", title: "Akontorechnung" },
	zahlungserinnerung: {
		type: "payment_reminder",
		title: "Zahlungserinnerung",
	},
	mahnung_1: { type: "first_reminder", title: "Erste Mahnung" },
	mahnung_2: { type: "follow_up_reminder", title: "Folgemahnung" },
	letzte_mahnung: { type: "final_reminder", title: "Letzte Mahnung" },
	auftragsblatt: { type: "order_sheet", title: "Auftragsblatt" },
	srg: { type: "collective_invoice", title: "Sammelrechnung" },
	etik_auftrag: { type: "label_order", title: "Auftragsetikett" },
};

export function getTestDocumentComponent(
	documentType: string,
	organization: OrganizationEntityType,
	pdfTemplateConfiguration: PdfTemplateConfiguration,
	labRegion: Region | null
): React.ReactElement {
	const docMeta = DOC_TYPE_MAP[documentType] ?? DOC_TYPE_MAP.default;
	const { today, client, ...commonProps } = getBaseProps(
		organization,
		pdfTemplateConfiguration,
		docMeta.type,
		docMeta.type === "delivery_note"
			? getDeliveryNoteTitle(labRegion)
			: docMeta.title
	);

	const paymentSlipData = getTestPaymentSlipData(client);
	const baseWithClient = { ...commonProps, client };

	switch (documentType) {
		case "kv":
			return React.createElement(
				KostenvoranschlagTemplate,
				baseWithClient
			);

		case "gs":
			return React.createElement(GutschriftTemplate, baseWithClient);

		case "rg":
		case "rg_doppel":
			return React.createElement(RechnungTemplate, {
				...baseWithClient,
				paymentSlipData,
				noPaymentSlip: true,
			});

		case "bar":
			return React.createElement(BarzahlungsquittungTemplate, {
				...baseWithClient,
				paymentSlipData,
			});

		case "karte":
			return React.createElement(KartenzahlungsquittungTemplate, {
				...baseWithClient,
				paymentSlipData,
			});

		case "akonto":
			return React.createElement(AkontorechnungTemplate, {
				...baseWithClient,
				paymentSlipData,
				date: today,
				noPaymentSlip: true,
			});

		case PdfTemplateType.ZAHLUNGSERINNERUNG:
		case PdfTemplateType.ERSTE_MAHNUNG:
		case PdfTemplateType.FOLGEMAHNUNG:
		case PdfTemplateType.LETZTE_MAHNUNG:
			return React.createElement(PaymentReminderTemplate, {
				document: getPaymentReminderDocumentExample(
					documentType,
					client
				),
				organization: commonProps.organization,
				pdfTemplateConfiguration: commonProps.pdfTemplateConfiguration,
				bankAccount: paymentSlipData.bankAccount,
			});

		case "auftragsblatt":
			return React.createElement(AuftragsblattTemplate, {
				client,
				job: commonProps.job,
				organization: commonProps.organization,
				pdfTemplateConfiguration: commonProps.pdfTemplateConfiguration,
				qrCodeUrl: "",
				jobDocument: commonProps.jobDocument,
				jobItems: commonProps.jobItems,
			});

		case "etik_auftrag":
			return React.createElement(JobOrderLabelTemplate, {
				client,
				job: commonProps.job,
				organization: commonProps.organization,
				pdfTemplateConfiguration: commonProps.pdfTemplateConfiguration,
				qrCodeUrl: "",
			});

		// srg (Sammelrechnung) has a completely different data structure — fall through to default
		default:
			return React.createElement(LieferscheinTemplate, baseWithClient);
	}
}

export function getTestLieferscheinProps(
	organization: OrganizationEntityType,
	pdfTemplateConfiguration: PdfTemplateConfiguration
) {
	const { today: _, ...props } = getBaseProps(
		organization,
		pdfTemplateConfiguration,
		"delivery_note",
		"Lieferschein"
	);
	return props;
}
